import { gql, useMutation, useQuery } from "@apollo/client";
import {
  ArrowLeftOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import UnassignedOrderMap from "../components/UnassignedOrderMap";

const ADMIN_GET_ORDER_VIEW = gql`
  query AdminGetOrderView($orderId: Int!, $radiusKm: Float!) {
    adminGetOrderView(orderId: $orderId, radiusKm: $radiusKm) {
      order {
        id
        status
        storeName
        storeAddressCoordinates
        scheduledAt
        createdAt
        userPhoneNumber
        itemsCount
        store {
          id
          name
          nameAr
          latitude
          longitude
        }
        items {
          phoneNumber
          name
          items {
            id
            productName
            quantity
            totalAmount
            note
          }
        }
      }
      nearbyDrivers {
        driverId
        name
        phoneNumber
        vehicleNumber
        vehicleType
        driverStatus
        isOnline
        hasUncompletedDeliveryRequest
        distanceKm
        latitude
        longitude
      }
    }
  }
`;

const ADMIN_ASSIGN_DRIVER_TO_ORDER = gql`
  mutation AdminAssignDriverToOrder($orderId: Int!, $driverId: Int!) {
    adminAssignDriverToOrder(orderId: $orderId, driverId: $driverId) {
      id
      status
      driverId
      driver {
        id
        name
        phoneNumber
      }
    }
  }
`;

const ADMIN_CANCEL_ORDER = gql`
  mutation AdminCancelOrder($orderId: Int!, $cancellationNote: String) {
    adminCancelOrder(orderId: $orderId, cancellationNote: $cancellationNote) {
      id
      status
      cancellationNote
    }
  }
`;

type OrderViewItem = {
  id?: number | null;
  productName?: string | null;
  quantity?: number | null;
  totalAmount?: number | null;
  note?: string | null;
} | null;

type OrderViewGroup = {
  phoneNumber?: string | null;
  name?: string | null;
  items?: OrderViewItem[] | null;
} | null;

type OrderViewStore = {
  id?: number | null;
  name?: string | null;
  nameAr?: string | null;
  latitude?: number | null;
  longitude?: number | null;
} | null;

type OrderView = {
  id?: number | null;
  status?: string | null;
  storeName?: string | null;
  storeAddressCoordinates?: string | null;
  scheduledAt?: string | null;
  createdAt?: string | null;
  userPhoneNumber?: string | null;
  itemsCount?: number | null;
  store?: OrderViewStore;
  items?: OrderViewGroup[] | null;
} | null;

type NearbyDriver = {
  driverId?: number | null;
  name?: string | null;
  phoneNumber?: string | null;
  vehicleNumber?: string | null;
  vehicleType?: string | null;
  driverStatus?: string | null;
  isOnline?: boolean | null;
  hasUncompletedDeliveryRequest?: boolean | null;
  distanceKm?: number | null;
  latitude?: number | null;
  longitude?: number | null;
} | null;

type AdminGetOrderViewResponse = {
  adminGetOrderView?: {
    order?: OrderView;
    nearbyDrivers?: NearbyDriver[] | null;
  } | null;
};

type AdminAssignDriverToOrderResponse = {
  adminAssignDriverToOrder?: {
    id?: number | null;
    status?: string | null;
    driverId?: number | null;
    driver?: {
      id?: number | null;
      name?: string | null;
      phoneNumber?: string | null;
    } | null;
  } | null;
};

type AdminCancelOrderResponse = {
  adminCancelOrder?: {
    id?: number | null;
    status?: string | null;
    cancellationNote?: string | null;
  } | null;
};

type CancelOrderFormValues = {
  cancellationNote: string;
};

const DEFAULT_RADIUS_KM = 5;

function parseCoordinates(
  value?: string | null,
): { latitude: number; longitude: number } | null {
  if (!value) return null;

  const normalized = value.trim();

  const pointMatch = normalized.match(
    /POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)/i,
  );
  if (pointMatch) {
    const longitude = Number(pointMatch[1]);
    const latitude = Number(pointMatch[2]);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { latitude, longitude };
    }
  }

  const parts = normalized.split(/[,;\s]+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = Number(parts[0]);
    const second = Number(parts[1]);
    if (!Number.isFinite(first) || !Number.isFinite(second)) {
      return null;
    }

    // GeoJSON / "lng lat" usually has longitude first (|lng| can be > 90).
    if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
      return { latitude: second, longitude: first };
    }

    return { latitude: first, longitude: second };
  }

  return null;
}

function UnassignedOrderDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [cancelForm] = Form.useForm<CancelOrderFormValues>();

  const orderId = Number(id);
  const [radiusKmInput, setRadiusKmInput] = useState<number | null>(DEFAULT_RADIUS_KM);
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  const [assigningDriverId, setAssigningDriverId] = useState<number | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<AdminGetOrderViewResponse>(
    ADMIN_GET_ORDER_VIEW,
    {
      variables: {
        orderId,
        radiusKm,
      },
      skip: !Number.isFinite(orderId),
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  const [assignDriverToOrder] = useMutation<AdminAssignDriverToOrderResponse>(
    ADMIN_ASSIGN_DRIVER_TO_ORDER,
    {
      onCompleted: (response) => {
        const assignedDriverName =
          response.adminAssignDriverToOrder?.driver?.name ||
          response.adminAssignDriverToOrder?.driverId;

        message.success(
          t("message.success.order_assigned_to_driver", {
            driver: assignedDriverName ?? "-",
          }),
        );
        setAssigningDriverId(null);
        navigate("/orders/unassigned");
      },
      onError: (mutationError) => {
        setAssigningDriverId(null);
        message.error(mutationError.message || t("message.error.generic"));
      },
    },
  );

  const [cancelOrder, { loading: cancellingOrder }] =
    useMutation<AdminCancelOrderResponse>(ADMIN_CANCEL_ORDER, {
      onCompleted: () => {
        message.success(t("message.success.order_cancelled"));
        setIsCancelModalOpen(false);
        cancelForm.resetFields();
        navigate("/orders/unassigned");
      },
      onError: (mutationError) => {
        message.error(mutationError.message || t("message.error.generic"));
      },
    });

  const handleAssignDriver = async (driverId?: number | null) => {
    if (!Number.isFinite(orderId) || !driverId) return;

    setAssigningDriverId(driverId);
    await assignDriverToOrder({
      variables: {
        orderId,
        driverId,
      },
    });
  };

  const handleOpenCancelModal = () => {
    cancelForm.setFieldsValue({
      cancellationNote: t("label.order.default_cancellation_note"),
    });
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
    cancelForm.resetFields();
  };

  const handleCancelOrder = async (values: CancelOrderFormValues) => {
    if (!Number.isFinite(orderId)) return;

    await cancelOrder({
      variables: {
        orderId,
        cancellationNote: values.cancellationNote.trim(),
      },
    });
  };

  const handleApplyRadiusFilter = () => {
    const nextRadius =
      radiusKmInput !== null && Number.isFinite(radiusKmInput) && radiusKmInput > 0
        ? radiusKmInput
        : DEFAULT_RADIUS_KM;

    setRadiusKmInput(nextRadius);
    setRadiusKm(nextRadius);
  };

  const handleResetRadiusFilter = () => {
    setRadiusKmInput(DEFAULT_RADIUS_KM);
    setRadiusKm(DEFAULT_RADIUS_KM);
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "-";

    const parsedValue = dayjs(value);
    return parsedValue.isValid() ? parsedValue.format("DD/MM/YYYY HH:mm") : value;
  };

  const getStatusColor = (status?: string | null) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "warning";
      case "ASSIGNED":
        return "processing";
      case "CANCELLED":
        return "error";
      case "COMPLETED":
        return "success";
      default:
        return "default";
    }
  };

  const orderView = data?.adminGetOrderView?.order;
  const nearbyDrivers = data?.adminGetOrderView?.nearbyDrivers ?? [];

  const storeCoordinates = useMemo(() => {
    const storeLat = Number(orderView?.store?.latitude);
    const storeLng = Number(orderView?.store?.longitude);

    if (Number.isFinite(storeLat) && Number.isFinite(storeLng)) {
      return { latitude: storeLat, longitude: storeLng };
    }

    return parseCoordinates(orderView?.storeAddressCoordinates);
  }, [
    orderView?.store?.latitude,
    orderView?.store?.longitude,
    orderView?.storeAddressCoordinates,
  ]);

  const orderItemsDataSource =
    orderView?.items?.flatMap((group, groupIndex) =>
      (group?.items ?? []).map((item, itemIndex) => ({
        key: `${group?.phoneNumber ?? groupIndex}-${item?.id ?? itemIndex}`,
        customerName: group?.name ?? "-",
        customerPhoneNumber: group?.phoneNumber ?? "-",
        productName: item?.productName ?? "-",
        quantity: item?.quantity ?? 0,
        totalAmount: item?.totalAmount ?? "-",
        note: item?.note ?? "-",
      })),
    ) ?? [];

  const nearbyDriversDataSource = nearbyDrivers.map((driver, index) => ({
    key: driver?.driverId ?? index,
    driverId: driver?.driverId ?? null,
    name: driver?.name ?? "-",
    phoneNumber: driver?.phoneNumber ?? "-",
    vehicleNumber: driver?.vehicleNumber ?? "-",
    vehicleType: driver?.vehicleType ?? "-",
    driverStatus: driver?.driverStatus ?? "-",
    isOnline: driver?.isOnline,
    hasUncompletedDeliveryRequest: driver?.hasUncompletedDeliveryRequest,
    distanceKm:
      driver?.distanceKm === null || driver?.distanceKm === undefined
        ? "-"
        : Number(driver.distanceKm).toFixed(2),
  }));

  const orderItemsColumns = [
    {
      title: t("label.name"),
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: t("label.order.user_phone_number"),
      dataIndex: "customerPhoneNumber",
      key: "customerPhoneNumber",
    },
    {
      title: t("label.order.product_name"),
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: t("label.order.quantity"),
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
    },
    {
      title: t("label.order.total_amount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 120,
    },
    {
      title: t("label.order.note"),
      dataIndex: "note",
      key: "note",
    },
  ];

  const nearbyDriversColumns = [
    {
      title: "ID",
      dataIndex: "driverId",
      key: "driverId",
      width: 90,
    },
    {
      title: t("label.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("label.phone"),
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: t("label.driver.vehicle_number"),
      dataIndex: "vehicleNumber",
      key: "vehicleNumber",
    },
    {
      title: t("label.driver.vehicle_type"),
      dataIndex: "vehicleType",
      key: "vehicleType",
    },
    {
      title: t("label.order.has_uncompleted_delivery_request"),
      dataIndex: "hasUncompletedDeliveryRequest",
      key: "hasUncompletedDeliveryRequest",
      render: (value: boolean | null | undefined) =>
        value === null || value === undefined
          ? "-"
          : value
            ? t("label.yes")
            : t("label.no"),
    },
    {
      title: t("label.order.distance_km"),
      dataIndex: "distanceKm",
      key: "distanceKm",
    },
    {
      title: t("label.actions"),
      key: "actions",
      width: 120,
      align: "center" as const,
      render: (
        _: unknown,
        record: { driverId: number | null; name: string },
      ) => (
        <Popconfirm
          title={t("label.order.assign_driver_confirm", {
            driver: record.name,
          })}
          okText={t("button.confirm")}
          cancelText={t("button.cancel")}
          disabled={!record.driverId}
          onConfirm={() => handleAssignDriver(record.driverId)}
        >
          <Tooltip title={t("button.assign_driver")}>
            <Button
              type="primary"
              shape="circle"
              icon={<UserAddOutlined />}
              disabled={!record.driverId}
              loading={assigningDriverId === record.driverId}
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  if (!Number.isFinite(orderId)) {
    return (
      <RobinPage title={t("label.order.details")}>
        <Typography.Text type="danger">{t("error.failed_to_load")}</Typography.Text>
      </RobinPage>
    );
  }

  return (
    <RobinPage
      title={`${t("label.order.details")} #${orderId}`}
      actions={
        <Space size="middle">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/orders/unassigned")}>
            {t("button.back")}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            disabled={loading || cancellingOrder}
          >
            {t("button.refresh")}
          </Button>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            onClick={handleOpenCancelModal}
            disabled={loading || cancellingOrder}
            loading={cancellingOrder}
          >
            {t("button.cancel_order")}
          </Button>
        </Space>
      }
    >
      {error ? (
        <Typography.Text type="danger">
          {error.message || t("error.failed_to_load")}
        </Typography.Text>
      ) : null}

      <Modal
        title={t("label.order.cancel_order")}
        open={isCancelModalOpen}
        onCancel={handleCloseCancelModal}
        footer={null}
        destroyOnHidden
      >
        <Form<CancelOrderFormValues>
          form={cancelForm}
          layout="vertical"
          onFinish={handleCancelOrder}
        >
          <Form.Item
            name="cancellationNote"
            label={t("label.order.cancellation_note")}
            rules={[
              {
                required: true,
                message: t("message.error.field_required"),
              },
              {
                whitespace: true,
                message: t("message.error.field_required"),
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder={t("label.order.cancellation_note")}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseCancelModal} disabled={cancellingOrder}>
                {t("button.cancel")}
              </Button>
              <Button type="primary" danger htmlType="submit" loading={cancellingOrder}>
                {t("button.cancel_order")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card loading={loading}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label={t("label.order.id")}>
              {orderView?.id ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("label.status")}>
              {orderView?.status ? (
                <Tag color={getStatusColor(orderView.status)}>{orderView.status}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("label.store.name")}>
              {orderView?.storeName ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("label.order.items_count")}>
              {orderView?.itemsCount ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("label.order.scheduled_at")}>
              {formatDateTime(orderView?.scheduledAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t("label.created_at")}>
              {formatDateTime(orderView?.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t("label.order.user_phone_number")} span={2}>
              {orderView?.userPhoneNumber ?? "-"}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title={t("label.order.items")} loading={loading}>
          <Table
            dataSource={orderItemsDataSource}
            columns={orderItemsColumns}
            pagination={false}
            locale={{ emptyText: t("label.no_data") }}
            scroll={{ x: 800 }}
          />
        </Card>

        <Card title={t("label.order.map")}>
          {loading ? (
            <div
              style={{
                height: 420,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography.Text type="secondary">{t("label.loading")}</Typography.Text>
            </div>
          ) : storeCoordinates ||
            nearbyDrivers.some(
              (driver) =>
                driver?.latitude !== null &&
                driver?.latitude !== undefined &&
                driver?.longitude !== null &&
                driver?.longitude !== undefined,
            ) ? (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Flex gap="middle" wrap="wrap">
                <Tag color="blue">{t("label.order.store_location")}</Tag>
                <Tag color="green">{t("label.order.nearby_drivers")}</Tag>
              </Flex>
              <UnassignedOrderMap
                storeName={orderView?.storeName || orderView?.store?.name}
                storeLatitude={storeCoordinates?.latitude}
                storeLongitude={storeCoordinates?.longitude}
                drivers={nearbyDrivers}
                assigningDriverId={assigningDriverId}
                onAssignDriver={handleAssignDriver}
              />
            </Space>
          ) : (
            <Typography.Text type="secondary">{t("label.no_data")}</Typography.Text>
          )}
        </Card>

        <Card
          title={`${t("label.order.nearby_drivers")} (${radiusKm} km)`}
          loading={loading}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Flex gap="middle" wrap="wrap" align="center">
              <InputNumber
                min={0.01}
                step={0.01}
                value={radiusKmInput}
                placeholder={t("label.order.radius_km")}
                addonAfter="km"
                onChange={(value) => setRadiusKmInput(value)}
                onPressEnter={handleApplyRadiusFilter}
                style={{ width: 220 }}
              />
              <Space.Compact>
                <Button type="primary" onClick={handleApplyRadiusFilter}>
                  {t("button.search")}
                </Button>
                <Button onClick={handleResetRadiusFilter}>
                  {t("button.reset_filters")}
                </Button>
              </Space.Compact>
            </Flex>

            <Table
              dataSource={nearbyDriversDataSource}
              columns={nearbyDriversColumns}
              pagination={false}
              locale={{ emptyText: t("label.no_data") }}
              scroll={{ x: 1000 }}
            />
          </Space>
        </Card>
      </Space>
    </RobinPage>
  );
}

export default UnassignedOrderDetails;
